import React, { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation.ts';
import { AITrendIcon, ShieldCheckIcon, ChartBarIcon, BoltIcon, UsersIcon, CogIcon } from './Icons.tsx';

const featuresData = [
  {
    icon: <ChartBarIcon className="h-8 w-8 text-orange-600 dark:text-[#FF4D00]" />,
    title: "Overview Dashboard",
    description: "Get a bird's-eye view of your entire inventory with real-time analytics, status metrics, and configurable alerts.",
    image: "https://ventro.energy/dashboardeq.jpg"
  },
  {
    icon: <ShieldCheckIcon className="h-8 w-8 text-orange-600 dark:text-[#FF4D00]" />,
    title: "Inspection Management",
    description: "Run digital inspections, track validity with color-coded statuses, and generate PDF certificates to maintain 95%+ compliance.",
    image: "https://ventro.energy/inspection.jpg"
  },
  {
    icon: <UsersIcon className="h-8 w-8 text-orange-600 dark:text-[#FF4D00]" />,
    title: "Assignment Management",
    description: "Easily assign equipment to users or projects. Track assignments, manage returns, and maintain a clear chain of custody.",
    image: "https://ventro.energy/assigment.jpg"
  },
  {
    icon: <AITrendIcon className="h-8 w-8 text-orange-600 dark:text-[#FF4D00]" />,
    title: "Complete Equipment Logbook",
    description: "Access a full audit trail with 13+ event types automatically logged, from inspections and assignments to service history.",
    image: "https://ventro.energy/logbook.jpg"
  },
  {
    icon: <BoltIcon className="h-8 w-8 text-orange-600 dark:text-[#FF4D00]" />,
    title: "Mobile Access & QR Codes",
    description: "Equip your team with our mobile app. Scan QR codes to instantly access equipment details and perform actions in the field.",
    image: "https://ventro.energy/mobline.jpg"
  }
];

const Features: React.FC = () => {
  const { ref: sectionHeaderRef, isVisible } = useScrollAnimation<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section id="features" className="bg-gray-50 dark:bg-[#0A0A0A] py-24">
      <div ref={sectionHeaderRef} className={`container mx-auto px-6 scroll-animation ${isVisible ? 'visible' : ''}`}>
        <div className="text-center max-w-2xl mx-auto mb-16 lg:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">A Complete Toolkit for Equipment Management</h2>
          <p className="text-gray-700 dark:text-gray-400">
            Shield provides over 150 features across 11 categories, designed to give you total control, ensure safety, and boost operational efficiency.
          </p>
        </div>
      </div>
        
      <div className="container mx-auto px-6 hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
        {/* Left Column: Feature List */}
        <div className="flex flex-col gap-y-4">
          {featuresData.map((feature, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`p-6 rounded-xl text-left transition-all duration-300 border ${
                activeIndex === index
                  ? 'bg-white dark:bg-black/20 shadow-lg shadow-orange-600/10 dark:shadow-2xl dark:shadow-[#FF4D00]/20 border-orange-500 dark:border-[#FF4D00]'
                  : 'bg-transparent dark:bg-transparent border-gray-200 dark:border-white/10 hover:bg-white/5'
              }`}
            >
              <div className="flex items-start gap-4 mb-2">
                {feature.icon}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-400 text-base pl-12">{feature.description}</p>
            </button>
          ))}
        </div>

        {/* Right Column: Sticky Feature Image */}
        <div className="lg:sticky lg:top-28 h-full">
          <div className="relative w-full h-full flex items-center">
            <div className="relative w-full aspect-[4/3] max-h-[500px]">
              {featuresData.map((feature, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${activeIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-contain rounded-2xl shadow-2xl shadow-gray-900/20 dark:shadow-black/50"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
                  <div className="absolute bottom-6 left-6">
                    <h3 className="text-2xl font-bold text-white [text-shadow:0_1px_5px_rgba(0,0,0,0.5)]">{feature.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

       {/* Mobile view fallback - simple vertical list */}
       <div className="container mx-auto px-6 lg:hidden flex flex-col gap-y-12">
          {featuresData.map((feature, index) => (
            <div 
              key={`mobile-${index}`}
              className="bg-white dark:bg-black/20 p-6 rounded-xl shadow-lg"
            >
              <div className="flex items-start gap-4 mb-2">
                {feature.icon}
                <h3 className="text-xl font-semibold">{feature.title}</h3>
              </div>
              <p className="text-base pl-12 mb-4">{feature.description}</p>
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full object-contain rounded-xl shadow-lg aspect-video bg-gray-900"
              />
            </div>
          ))}
        </div>
    </section>
  );
};

export default Features;